'use client';

import { Typography } from '@jouzen/ecom-components/v3/Typography';
import { cx } from 'class-variance-authority';
import min from 'lodash/min';
import { useTranslations } from 'next-intl';
import { useMemo, useState } from 'react';

import PaginatedListControls, {
  PaginatedListControlsProps,
} from './PaginatedListControls';

export const TEST_ID = 'paginated-list';

export interface PaginatedListProps {
  readonly children: (item: any, i: number, arr: any[]) => JSX.Element;
  readonly className?: string;
  readonly list: unknown[];
  readonly firstPage?: number;
  readonly itemsPerPage?: number;
}

const PaginatedList = ({
  children,
  className,
  list,
  firstPage = 1,
  itemsPerPage = 10,
}: PaginatedListProps): JSX.Element => {
  const t = useTranslations();
  const [currentPage, setCurrentPage] = useState<number>(firstPage);

  // items
  const totalItemCount = list?.length;

  // page count
  const totalPageCount = Math.ceil(totalItemCount / itemsPerPage);
  const isMultiPage = totalPageCount > 1;

  // relative page status
  const isFirstPage = currentPage === 1;
  const isLastPage = currentPage === totalPageCount;

  // ui elements
  const endNumber =
    min([currentPage * itemsPerPage, totalItemCount]) ?? totalItemCount;
  const startIndex = currentPage * itemsPerPage - itemsPerPage;
  const currentPageItems = list.slice(startIndex, endNumber);

  const controlProps: PaginatedListControlsProps = useMemo(
    () => ({
      isFirstPage: isFirstPage,
      isLastPage: isLastPage,
      onNext: () => setCurrentPage((cur) => cur + 1),
      onPrev: () => setCurrentPage((cur) => cur - 1),
    }),
    [isFirstPage, isLastPage],
  );

  return (
    <div data-testid={TEST_ID} className={cx(className, 'flex flex-col gap-4')}>
      {isMultiPage ? (
        <div
          className={cx(
            'flex flex-wrap items-center justify-between gap-4',
            'pb-4',
            'border-b border-gray-300',
          )}
        >
          <aside className="w-max">
            <Typography Element="span" variant="h6">
              {t.rich('pagination_label_start_end_total', {
                startNumber: startIndex + 1,
                endNumber,
                totalCount: totalItemCount,
              })}
            </Typography>
          </aside>

          <PaginatedListControls
            {...controlProps}
            className={cx(controlProps.className, 'grow')}
          />
        </div>
      ) : null}

      <ol className="flex size-full flex-col justify-stretch">
        {currentPageItems.map((item, idx, arr) => (
          <li key={currentPage + idx}>{children(item, idx, arr)}</li>
        ))}
      </ol>

      {isMultiPage ? (
        <PaginatedListControls
          {...controlProps}
          className={cx(
            controlProps.className,
            'border-t border-gray-300 pt-4',
          )}
        />
      ) : null}
    </div>
  );
};

export default PaginatedList;
